// Quick find (Ctrl+K or Cmd+K, or the button in the sidebar): one box for
// people, clients, pages and the things people start most. Before you type it
// shows what you opened last. Arrow keys move, Enter goes, Escape closes.
import { MagnifyingGlass } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useDeferredValue, useEffect, useId, useRef, useState } from "react";
import { api, type Me } from "../lib/api.ts";
import { recents } from "../lib/recent.ts";
import { Button } from "./ui.tsx";

interface Hit {
	href: string;
	label: string;
	kind: string;
}

const PLACES: (Hit & { permission?: string; words: string })[] = [
	{
		href: "/bookings/new",
		label: "Book shifts",
		kind: "Start",
		permission: "bookings.edit",
		words: "add book shifts order",
	},
	{
		href: "/candidates/new",
		label: "Add a candidate",
		kind: "Start",
		permission: "candidates.edit",
		words: "new person register",
	},
	{
		href: "/tasks",
		label: "Tasks",
		kind: "Page",
		permission: "tasks.manage",
		words: "inbox todo waiting what next",
	},
	{ href: "/plan", label: "Plan", kind: "Page", permission: "bookings.view", words: "week rota schedule" },
	{ href: "/timesheets", label: "Timesheets", kind: "Page", permission: "timesheets.view", words: "hours approve" },
	{ href: "/payroll", label: "Payroll", kind: "Page", permission: "payroll.export", words: "evertime pay file" },
	{ href: "/rates", label: "Rate cards", kind: "Page", permission: "rate_cards.view", words: "pay charge rates" },
	{ href: "/reports", label: "Reports", kind: "Page", permission: "reports.view", words: "numbers margin fill" },
	{ href: "/vacancies", label: "Permanent jobs", kind: "Page", permission: "bookings.view", words: "vacancy perm" },
	{
		href: "/compliance",
		label: "Check documents",
		kind: "Page",
		permission: "compliance.verify",
		words: "documents queue",
	},
	{
		href: "/settings/ways",
		label: "Ways of working",
		kind: "Settings",
		permission: "agency.settings.manage",
		words: "automation reminders",
	},
	{
		href: "/settings/team",
		label: "Your team",
		kind: "Settings",
		permission: "agency.users.manage",
		words: "invite users seats",
	},
	{
		href: "/settings/forms",
		label: "Forms",
		kind: "Settings",
		permission: "agency.forms.manage",
		words: "registration onboarding",
	},
	{
		href: "/imports",
		label: "Import a spreadsheet",
		kind: "Start",
		permission: "agency.imports.run",
		words: "excel csv upload",
	},
	{ href: "/account", label: "Your account and passkeys", kind: "Settings", words: "security sign in" },
];

export function openQuickFind() {
	window.dispatchEvent(new Event("quick-find"));
}

export function QuickFind({ me }: { me: Me }) {
	const [open, setOpen] = useState(false);
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				e.preventDefault();
				setOpen((o) => !o);
			}
		};
		const onOpen = () => setOpen(true);
		window.addEventListener("keydown", onKey);
		window.addEventListener("quick-find", onOpen);
		return () => {
			window.removeEventListener("keydown", onKey);
			window.removeEventListener("quick-find", onOpen);
		};
	}, []);
	return open ? <Finder me={me} onClose={() => setOpen(false)} /> : null;
}

function Finder({ me, onClose }: { me: Me; onClose: () => void }) {
	const navigate = useNavigate();
	const [q, setQ] = useState("");
	const [active, setActive] = useState(0);
	const search = useDeferredValue(q.trim());
	const input = useRef<HTMLInputElement>(null);
	const listId = useId();
	const can = (p?: string) => !p || me.permissions.includes(p);
	useEffect(() => input.current?.focus(), []);

	const people = useQuery({
		queryKey: ["quick-find", "candidates", search],
		queryFn: () =>
			api<{ items: { id: string; first_name: string; last_name: string; town: string | null }[] }>(
				`/candidates?limit=5&q=${encodeURIComponent(search)}`,
			),
		enabled: search.length >= 2,
		staleTime: 15_000,
	});
	const clients = useQuery({
		queryKey: ["quick-find", "clients", search],
		queryFn: () => api<{ items: { id: string; name: string }[] }>(`/clients?limit=4&q=${encodeURIComponent(search)}`),
		enabled: search.length >= 2,
		staleTime: 15_000,
	});

	const words = search.toLowerCase();
	const places = PLACES.filter(
		(p) => can(p.permission) && (!words || `${p.label} ${p.words}`.toLowerCase().includes(words)),
	);
	const hits: Hit[] = search
		? [
				...(people.data?.items ?? []).map((c) => ({
					href: `/candidates/${c.id}`,
					label: `${c.first_name} ${c.last_name}${c.town ? `, ${c.town}` : ""}`,
					kind: "Candidate",
				})),
				...(clients.data?.items ?? []).map((c) => ({ href: `/clients/${c.id}`, label: c.name, kind: "Client" })),
				...places.slice(0, 5),
			]
		: [
				...recents()
					.slice(0, 5)
					.map((r) => ({ href: r.href, label: r.label, kind: "Recent" })),
				...places.slice(0, 6),
			];
	const go = (h: Hit | undefined) => {
		if (!h) return;
		onClose();
		void navigate({ to: h.href });
	};

	return (
		<div className="fixed inset-0 z-50 flex items-start justify-center bg-navy/50 px-4 pt-[12vh]">
			<button
				type="button"
				aria-label="Close quick find"
				className="absolute inset-0 cursor-default"
				onClick={onClose}
			/>
			<div
				role="dialog"
				aria-modal="true"
				aria-label="Quick find"
				className="relative w-full max-w-xl overflow-hidden rounded-[var(--radius-card)] bg-card shadow-2xl"
			>
				<div className="flex items-center gap-3 border-b-2 border-rule px-4">
					<MagnifyingGlass size={22} weight="bold" aria-hidden="true" className="shrink-0 text-ink-soft" />
					<input
						ref={input}
						role="combobox"
						aria-expanded="true"
						aria-controls={listId}
						aria-activedescendant={hits[active] ? `${listId}-${active}` : undefined}
						aria-label="Find a person, client or page"
						placeholder="Find a person, client or page"
						value={q}
						onChange={(e) => {
							setQ(e.target.value);
							setActive(0);
						}}
						onKeyDown={(e) => {
							if (e.key === "Escape") onClose();
							else if (e.key === "ArrowDown") {
								e.preventDefault();
								setActive((a) => Math.min(a + 1, hits.length - 1));
							} else if (e.key === "ArrowUp") {
								e.preventDefault();
								setActive((a) => Math.max(a - 1, 0));
							} else if (e.key === "Enter") go(hits[active]);
						}}
						className="min-h-14 w-full bg-transparent text-lg outline-none"
					/>
					{/* Phones have no Escape key. */}
					<Button variant="quiet" onClick={onClose} className="shrink-0 sm:hidden">
						Close
					</Button>
				</div>
				<div id={listId} role="listbox" aria-label="Results" className="max-h-[50vh] overflow-y-auto p-2">
					{hits.length === 0 ? (
						<p className="px-3 py-4 text-ink-soft">
							{people.isFetching || clients.isFetching ? "Looking" : "Nothing matches. Try a surname or postcode."}
						</p>
					) : (
						hits.map((h, i) => (
							<div
								key={`${h.kind}-${h.href}`}
								tabIndex={-1}
								id={`${listId}-${i}`}
								role="option"
								aria-selected={i === active}
								onMouseEnter={() => setActive(i)}
								onClick={() => go(h)}
								onKeyDown={() => undefined}
								className={`flex min-h-12 cursor-pointer items-center justify-between gap-3 rounded-xl px-3 ${i === active ? "bg-mint/40" : ""}`}
							>
								<span className="truncate font-semibold">{h.label}</span>
								<span className="shrink-0 text-sm text-ink-soft">{h.kind}</span>
							</div>
						))
					)}
				</div>
				<p className="hidden border-t-2 border-rule px-4 py-2 text-sm text-ink-soft sm:block">
					Arrow keys to move, Enter to open, Escape to close.
				</p>
			</div>
		</div>
	);
}
